import { FunctionComponent, KeyboardEvent, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import {
	faLifeRing,
	faPaperPlane,
	faThumbsDown,
	faThumbsUp,
} from "@fortawesome/pro-regular-svg-icons";

import { HelpChatFeedback } from "@signalist/shared-types";

import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import { classNames } from "@/utils/classNames";
import { Icon } from "@/components/Icon/Icon";
import { MarkdownContent } from "@/components/MarkdownContent/MarkdownContent";

import { useSetHelpFeedbackMutation } from "@/pages/Private/redux/helpCenter/helpConversations.api";
import {
	HelpChatHistoryTurn,
	HelpChatPageContext,
} from "@/pages/Private/redux/helpCenter/helpCenter.schema";
import {
	consumePendingQuestion,
	helpCenterSelector,
} from "@/pages/Private/redux/helpCenter/helpCenter.slice";

import { AiAnswerBadge, AiDisclosure } from "./AiDisclosure";
import { chooseAnswerLanguage } from "./messageLanguage";
import { helpChatErrorKey } from "./sseFrames";
import { SupportTicketModal } from "./SupportTicketModal";
import { HelpChatUiMessage } from "./types";
import { useHelpChatStream } from "./useHelpChatStream";

/** A pending support ticket the user is composing (null when the modal is closed). */
interface TicketDraft {
	subject: string;
}

interface HelpChatProps {
	pageContext: HelpChatPageContext;
	/** Starter questions shown as chips while the conversation is empty. */
	suggestions?: string[];
}

/**
 * Whether the answer being streamed is still empty — the assistant reads articles for seconds
 * before it writes a word, with no heartbeat, so the dots cover that gap and stop the moment the
 * first fragment lands.
 */
const lastAnswerIsEmpty = (messages: HelpChatUiMessage[]): boolean => {
	const last = messages.at(-1);

	return last?.role === "assistant" && last.content === "";
};

/** Animated "assistant is typing" indicator (three pulsing dots). */
const TypingDots: FunctionComponent = () => (
	<span className="flex items-center gap-1 py-1.5">
		{[0, 1, 2].map((index) => (
			<span
				className="h-1.5 w-1.5 animate-bounce rounded-full bg-text-meta"
				key={index}
				style={{ animationDelay: `${index * 150}ms` }}
			/>
		))}
	</span>
);

/** One rendered chat turn — user bubble (right) or assistant answer (left) with source links. */
const MessageBubble: FunctionComponent<{
	message: HelpChatUiMessage;
	sourcesLabel: string;
	escalateLabel: string;
	feedbackUpLabel: string;
	feedbackDownLabel: string;
	onCreateTicket: () => void;
	onFeedback: (message: HelpChatUiMessage, feedback: HelpChatFeedback) => void;
}> = ({
	message,
	sourcesLabel,
	escalateLabel,
	feedbackUpLabel,
	feedbackDownLabel,
	onCreateTicket,
	onFeedback,
}) => {
	if (message.role === "user") {
		return (
			<div className="flex justify-end">
				<div className="max-w-[85%] rounded-lg bg-blue-action px-3 py-2 text-sm text-white">
					{message.content}
				</div>
			</div>
		);
	}

	return (
		<div className="flex flex-col gap-2">
			<AiAnswerBadge />
			<div className="max-w-[95%] rounded-lg bg-gray-50 px-3 py-2">
				<MarkdownContent className="text-sm text-text" content={message.content} />
			</div>
			{message.sources && message.sources.length > 0 && (
				<div className="flex flex-col gap-1 pl-1">
					<span className="text-xs font-semibold uppercase tracking-wide text-text-meta">
						{sourcesLabel}
					</span>
					<div className="flex flex-wrap gap-1.5">
						{message.sources.map((source) => (
							<a
								className="cursor-pointer rounded-full border border-border-light px-2.5 py-1 text-xs text-blue-action hover:bg-gray-50"
								href={source.url}
								key={source.slug}
								rel="noopener noreferrer"
								target="_blank"
							>
								{source.title}
							</a>
						))}
					</div>
				</div>
			)}
			{message.canEscalate && (
				<button
					className="flex w-fit cursor-pointer items-center gap-1.5 rounded-lg border border-brand-light px-2.5 py-1.5 text-xs font-semibold text-blue-action transition-colors duration-150 hover:bg-blue-action-200"
					onClick={onCreateTicket}
				>
					<Icon icon={faLifeRing} />
					{escalateLabel}
				</button>
			)}
			{message.messageId !== undefined && (
				<div className="flex items-center gap-1 pl-1">
					<button
						aria-label={feedbackUpLabel}
						className={classNames(
							"flex h-6 w-6 cursor-pointer items-center justify-center rounded text-xs transition-colors duration-150",
							message.feedback === HelpChatFeedback.UP
								? "text-status-success"
								: "text-text-meta hover:text-status-success"
						)}
						title={feedbackUpLabel}
						onClick={() => onFeedback(message, HelpChatFeedback.UP)}
					>
						<Icon icon={faThumbsUp} />
					</button>
					<button
						aria-label={feedbackDownLabel}
						className={classNames(
							"flex h-6 w-6 cursor-pointer items-center justify-center rounded text-xs transition-colors duration-150",
							message.feedback === HelpChatFeedback.DOWN
								? "text-status-error"
								: "text-text-meta hover:text-status-error"
						)}
						title={feedbackDownLabel}
						onClick={() => onFeedback(message, HelpChatFeedback.DOWN)}
					>
						<Icon icon={faThumbsDown} />
					</button>
				</div>
			)}
		</div>
	);
};

/** The help conversation: grounded answers with cited article deep links. */
export const HelpChat: FunctionComponent<HelpChatProps> = ({ pageContext, suggestions = [] }) => {
	const { t } = useTranslation();
	const dispatch = useAppDispatch();

	const { pendingQuestion } = useAppSelector(helpCenterSelector);

	const [messages, setMessages] = useState<HelpChatUiMessage[]>([]);
	const [input, setInput] = useState("");
	const [ticketDraft, setTicketDraft] = useState<TicketDraft | null>(null);
	const { send, isStreaming } = useHelpChatStream();
	const [setHelpFeedback] = useSetHelpFeedbackMutation();

	const scrollRef = useRef<HTMLDivElement>(null);
	const nextIdRef = useRef(0);
	// Server conversation id — undefined until the first answer comes back, then sent on every
	// following turn so all turns thread into one persisted conversation.
	const conversationIdRef = useRef<number | undefined>(undefined);
	/** The last turn's answer language — what a message too short to classify keeps. */
	const languageRef = useRef<string | null>(null);

	const nextId = () => {
		nextIdRef.current += 1;

		return nextIdRef.current;
	};

	useEffect(() => {
		scrollRef.current?.scrollTo?.({ top: scrollRef.current.scrollHeight });
	}, [messages, isStreaming]);

	/** Replace the answer bubble currently being streamed into. */
	const updateAnswer = (answerId: number, update: Partial<HelpChatUiMessage>) =>
		setMessages((prev) =>
			prev.map((message) => (message.id === answerId ? { ...message, ...update } : message))
		);

	const sendMessage = async (text: string) => {
		const trimmed = text.trim();

		if (!trimmed || isStreaming) {
			return;
		}

		const history = messages.map(({ role, content }) => ({ role, content }));
		const answerLanguage = chooseAnswerLanguage(trimmed, languageRef.current, navigator.language);

		languageRef.current = answerLanguage;
		const answerId = nextId() + 1;

		// The answer bubble is created empty and filled as fragments arrive, so the user sees the
		// answer being written rather than a spinner followed by a wall of text.
		setMessages((prev) => [
			...prev,
			{ id: nextId(), role: "user", content: trimmed },
			{ id: answerId, role: "assistant", content: "" },
		]);
		setInput("");
		nextIdRef.current = answerId;

		await send(
			{
				message: trimmed,
				pageContext,
				history,
				// The assistant answers in the locale it is given and infers none, so the turn carries
				// the language the user is writing in (PREC-4067).
				language: answerLanguage,
				conversationId: conversationIdRef.current,
			},
			{
				onDelta: (fragment) =>
					setMessages((prev) =>
						prev.map((message) =>
							message.id === answerId
								? { ...message, content: message.content + fragment }
								: message
						)
					),
				onComplete: (turn) => {
					conversationIdRef.current = turn.conversationId ?? conversationIdRef.current;
					updateAnswer(answerId, {
						sources: turn.sources,
						messageId: turn.messageId,
						canEscalate: turn.canEscalate,
					});
				},
				onError: (code) => updateAnswer(answerId, { content: t(helpChatErrorKey(code)) }),
			}
		);
	};

	// A question queued elsewhere (quick-question chips on the landing) is sent as soon as
	// the chat mounts / the question arrives.
	useEffect(() => {
		if (pendingQuestion) {
			dispatch(consumePendingQuestion());
			sendMessage(pendingQuestion);
		}
	}, [pendingQuestion]);

	const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
		if (event.key === "Enter" && !event.shiftKey) {
			event.preventDefault();
			sendMessage(input);
		}
	};

	// Rate an assistant answer (thumbs up/down). Optimistic: clicking the active rating clears it;
	// a failed request rolls the local state back. Only persisted answers (with a server messageId)
	// can be rated.
	const handleFeedback = async (message: HelpChatUiMessage, feedback: HelpChatFeedback) => {
		if (message.messageId === undefined) {
			return;
		}

		const previous = message.feedback;
		const nextFeedback = previous === feedback ? null : feedback;

		setMessages((prev) =>
			prev.map((item) =>
				item.id === message.id ? { ...item, feedback: nextFeedback ?? undefined } : item
			)
		);

		try {
			await setHelpFeedback({ messageId: message.messageId, feedback: nextFeedback }).unwrap();
		} catch {
			setMessages((prev) =>
				prev.map((item) => (item.id === message.id ? { ...item, feedback: previous } : item))
			);
		}
	};

	// Open the ticket modal, prefilling the subject with the most recent question the user asked.
	const openTicket = () => {
		const lastQuestion = [...messages].reverse().find((message) => message.role === "user");

		setTicketDraft({ subject: lastQuestion?.content ?? "" });
	};

	const transcript: HelpChatHistoryTurn[] = messages.map(({ role, content }) => ({
		role,
		content,
	}));

	return (
		<div className="flex h-full min-h-0 flex-col">
			<AiDisclosure />

			<div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-4 py-3" ref={scrollRef}>
				{messages.length === 0 && !isStreaming && (
					<div className="flex flex-col gap-3">
						<p className="text-sm text-text-meta">{t("helpCenter.chat.empty")}</p>
						{suggestions.length > 0 && (
							<div className="flex flex-wrap gap-1.5">
								{suggestions.map((question) => (
									<button
										className="cursor-pointer rounded-full border border-border-light px-2.5 py-1.5 text-left text-xs text-text-meta transition-colors duration-150 hover:border-brand-light hover:text-blue-action"
										key={question}
										onClick={() => sendMessage(question)}
									>
										{question}
									</button>
								))}
							</div>
						)}
					</div>
				)}

				{messages.map((message) => (
					<MessageBubble
						key={message.id}
						escalateLabel={t("helpCenter.support.escalate")}
						feedbackDownLabel={t("helpCenter.chat.feedbackDown")}
						feedbackUpLabel={t("helpCenter.chat.feedbackUp")}
						message={message}
						onCreateTicket={openTicket}
						onFeedback={handleFeedback}
						sourcesLabel={t("helpCenter.chat.sources")}
					/>
				))}

				{isStreaming && lastAnswerIsEmpty(messages) && <TypingDots />}
			</div>

			<div className="border-t border-border-light p-3">
				<div className="flex items-end gap-2">
					<textarea
						className={classNames(
							"max-h-32 min-h-[40px] flex-1 resize-none rounded-lg border border-gray-200 px-3 py-2 text-sm text-text",
							"duration-200 hover:border-brand-light focus:border-brand-light focus:outline-none"
						)}
						placeholder={t("helpCenter.chat.placeholder")}
						rows={1}
						value={input}
						onChange={(event) => setInput(event.target.value)}
						onKeyDown={handleKeyDown}
					/>
					<button
						aria-label={t("helpCenter.chat.send")}
						className="flex h-10 w-10 shrink-0 cursor-pointer items-center justify-center rounded-lg bg-blue-action text-white disabled:cursor-not-allowed disabled:opacity-50"
						disabled={!input.trim() || isStreaming}
						onClick={() => sendMessage(input)}
					>
						<Icon icon={faPaperPlane} />
					</button>
				</div>
				<button
					className="mt-2 flex cursor-pointer items-center gap-1.5 text-xs text-text-meta transition-colors duration-150 hover:text-blue-action"
					onClick={openTicket}
				>
					<Icon icon={faLifeRing} />
					{t("helpCenter.support.contact")}
				</button>
			</div>

			{ticketDraft && (
				<SupportTicketModal
					defaultSubject={ticketDraft.subject}
					onClose={() => setTicketDraft(null)}
					pageContext={pageContext}
					transcript={transcript}
				/>
			)}
		</div>
	);
};
